/**
 * Commit orchestration for the Vue compatibility release.
 *
 * Mirrors the React client's rules so the two frontends cannot diverge:
 * - the server owns price, discount and group membership; the client sends neither
 * - the accepted quote revision is sent with the passengers
 * - a lost response keeps its idempotency key so a retry never creates a second order
 * - a changed quote is surfaced for explicit acceptance, never silently re-priced
 */

export class CommitError extends Error {
  constructor(code, message, { uncertain = false, changed = false, view = null, fieldErrors = {} } = {}) {
    super(message)
    this.name = 'CommitError'
    this.code = code
    this.uncertain = uncertain
    /** The quote moved: the caller must show the new numbers and ask again. */
    this.changed = changed
    this.view = view
    this.fieldErrors = fieldErrors
  }
}

const CONFLICT_CODES = ['quote_changed', 'version_conflict']

/** Maps Vue passenger objects to the wire shape without rewriting what the customer typed. */
export function buildPassengers(members, { requiresPickup }) {
  if (!Array.isArray(members) || members.length === 0) {
    throw new CommitError('validation_error', 'Cần ít nhất một hành khách.')
  }
  return members.map((member, index) => {
    const raw = typeof member.name === 'string' ? member.name : ''
    if (!raw.trim()) throw new CommitError('validation_error', 'Vui lòng nhập họ tên hành khách.', { fieldErrors: { [`passengers.${index}.name`]: 'Vui lòng nhập họ tên.' } })
    const pickup = member.pickupLocationId ?? member.pickup_location_id ?? null
    if (!requiresPickup && pickup !== null) {
      throw new CommitError('validation_error', 'Vé độc lập không có điểm đón.', { fieldErrors: { [`passengers.${index}.pickup_location_id`]: 'Vé độc lập không có điểm đón.' } })
    }
    return {
      // Identity numbers stay strings so a leading zero is never lost.
      // Sent as typed: the server trims, the client never rewrites a customer's name.
      name: raw,
      phone: member.phone || null,
      email: member.email || null,
      birth_date: member.birthDate ?? member.birth_date ?? null,
      birth_year: member.birthYear ?? member.birth_year ?? null,
      pickup_location_id: pickup,
      gender: member.gender || null,
      cccd: member.cccd ?? null,
      cccd_address: member.cccdAddress ?? member.cccd_address ?? null,
    }
  })
}

export function createCommitFlow(api, selectionFlow) {
  // The last authoritative checkout view; its `quote` is what a commit may accept.
  let latestView = null
  let commitKeys = { quoteRevision: null, key: null }
  let keyCounter = 0

  function order() {
    const current = selectionFlow.checkout
    if (!current) throw new CommitError('no_checkout', 'Chưa có đơn để tạo.')
    return current
  }

  function nextKey(prefix) {
    keyCounter += 1
    return `${prefix}-${order().checkout_id}-${keyCounter}`
  }

  async function loadCheckout() {
    const view = await api.readCheckout(order().checkout_id)
    selectionFlow.adoptRevision?.(view.revision)
    return view
  }

  return {
    get quote() { return latestView ? latestView.quote || null : null },

    /** Applies or removes a discount through the server and adopts the returned revision. */
    async requestQuote(discountCode) {
      const current = order()
      try {
        const view = await api.quote(current.checkout_id, {
          expected_revision: current.revision,
          discount_code: discountCode === '' || discountCode === undefined ? null : discountCode,
        }, nextKey('quote'))
        selectionFlow.adoptRevision?.(view.revision)
        latestView = view
        return { changed: false, view }
      } catch (error) {
        if (error && CONFLICT_CODES.includes(error.code)) {
          const view = await loadCheckout()
          latestView = view
          return { changed: true, view }
        }
        throw error
      }
    },

    /** Creates the order. Safe to retry after an uncertain failure with the same key. */
    async commit(members, { requiresPickup = true } = {}) {
      const current = order()
      const passengers = buildPassengers(members, { requiresPickup })
      const view = latestView || (await loadCheckout())
      const quoteRevision = view.quote ? view.quote.quote_revision : null
      if (quoteRevision === null) throw new CommitError('quote_changed', 'Chưa có báo giá từ máy chủ.', { changed: true, view })
      if (commitKeys.quoteRevision !== quoteRevision) {
        commitKeys = { quoteRevision, key: nextKey('commit') }
      }
      try {
        const committed = await api.commit(current.checkout_id, { quote_revision: quoteRevision, passengers }, commitKeys.key)
        return committed
      } catch (error) {
        if (error && CONFLICT_CODES.includes(error.code)) {
          // The old key belongs to a quote the server no longer accepts.
          commitKeys = { quoteRevision: null, key: null }
          const latest = await loadCheckout()
          latestView = latest
          throw new CommitError(error.code, error.message || 'Báo giá đã thay đổi.', { changed: true, view: latest })
        }
        if (error && error.uncertain) {
          throw new CommitError('network_error', 'Chưa rõ kết quả tạo đơn. Hãy thử lại bằng cùng yêu cầu.', { uncertain: true })
        }
        throw new CommitError(error?.code || 'internal_error', error?.message || 'Không tạo được đơn.', {
          fieldErrors: error?.fieldErrors || {},
        })
      }
    },
  }
}
